import { FC, useState, MouseEvent, useEffect } from 'react';
import { getPages, getActivePage } from './utils';
import { Button, createTheme } from 'flowbite-react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '@acme/assets';
import { formatNumber } from '../../utils';

const arrowButtonTheme = createTheme({
  outlineColor: {
    gray: 'border-gray-200 hover:text-gray-700 hover:bg-gray-200 hover:border-gray-200',
  },
});

const pageTheme = createTheme({
  color: {
    purple:
      'bg-transparent text-gray-500 hover:bg-brand-purple-50 hover:text-brand-purple-900 focus:ring-brand-purple-100 focus:ring-2',
  },
});

interface PaginationWithEllipsisProps {
  /**
   * Общее число элементов
   */
  total: number;
  /**
   * Активный элемент пагинации по умолчанию
   */
  defaultCurrent?: number;
  /**
   * Текущая активная страница
   */
  currentPage?: number;
  /**
   * Количество видимых элементов. По умолчанию 20
   */
  pageSize?: number;
  /**
   * Вызывается, когда изменяется номер страницы
   */
  onChange?: ({ page }: { page: number }) => void;
  /**
   * Стилизация компонента
   */
  className?: string;
}

export const PaginationWithEllipsis: FC<PaginationWithEllipsisProps> = ({
  pageSize = 20,
  total,
  defaultCurrent = 1,
  currentPage,
  onChange,
  className,
}) => {
  const numberPages = Math.ceil(total / pageSize);
  const [activePage, setActivePage] = useState<number>(
    getActivePage(defaultCurrent, numberPages)
  );
  const pages = getPages(activePage, numberPages);

  useEffect(() => {
    if (currentPage) {
      const page = getActivePage(currentPage, numberPages);
      setActivePage(page);
    }
  }, [currentPage, numberPages]);

  const handleClickPage = (event: MouseEvent<HTMLButtonElement>) => {
    const value = Number((event.target as HTMLButtonElement).value);

    if (value !== activePage) {
      setActivePage(value);
      onChange?.({ page: value });
    }
  };

  const handleClickArrow = (increase: number) => () => {
    setActivePage((prev) => prev + increase);
    onChange?.({ page: activePage + increase });
  };

  return (
    <div
      className={twMerge(
        'flex justify-between gap-2 border-t border-gray-200 pt-5',
        className
      )}
      role="navigation"
      aria-label="Pagination Navigation"
    >
      <Button
        aria-label="move-left"
        outline
        theme={arrowButtonTheme}
        color="gray"
        onClick={handleClickArrow(-1)}
        disabled={activePage === 1}
      >
        <div className="flex gap-2 items-center">
          <Icon name="arrow-back" />
          Предыдущая
        </div>
      </Button>

      <nav className="flex">
        {pages.map((item, index) => {
          if (!Number(item)) {
            return (
              <div
                key={`${index}-ellipses`}
                className="h-10 w-10 flex items-center justify-center text-gray-500"
              >
                {item}
              </div>
            );
          }

          const isActive = activePage === Number(item);

          return (
            <Button
              theme={pageTheme}
              color="purple"
              key={item}
              value={item}
              onClick={handleClickPage}
              className={twMerge(
                isActive && 'bg-brand-purple-50 text-brand-purple-900'
              )}
              aria-current={isActive}
              aria-label={`go to page ${item}`}
            >
              {formatNumber(Number(item))}
            </Button>
          );
        })}
      </nav>

      <Button
        aria-label="move-right"
        onClick={handleClickArrow(1)}
        disabled={activePage === numberPages || numberPages <= 0}
        outline
        color="gray"
        theme={arrowButtonTheme}
      >
        <div className="flex gap-2 items-center">
          Следующая
          <Icon name="arrow-back" className="rotate-180" />
        </div>
      </Button>
    </div>
  );
};
